import React, { useState, useEffect } from "react";
import {
  Building2,
  Users,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Calendar,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { ScrollArea } from "../../components/ui/scroll-area";
import axios from "axios";
import logger from "../../utils/logger";

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState([]);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const [statsRes, revenueRes, subsRes, expiringRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/super-admin/dashboard/stats`,
          config
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/super-admin/dashboard/revenue?months=6`,
          config
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/super-admin/dashboard/subscription-stats`,
          config
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/super-admin/dashboard/expiring-subscriptions?days=7`,
          config
        ),
      ]);

      setStats(statsRes.data.data);
      setRevenue(revenueRes.data.data);
      setSubscriptionStats(subsRes.data.data);
      setExpiringSubscriptions(expiringRes.data.data);
    } catch (error) {
      logger.error("خطأ في جلب بيانات لوحة التحكم:", error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    gradient,
    trend,
    trendUp,
  }) => (
    <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300">
      <CardContent className="p-0">
        <div className={`bg-gradient-to-br ${gradient} p-6 text-white`}>
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <p className="text-white/80 text-sm font-medium">{title}</p>
              <p className="text-3xl font-bold">{value}</p>
              {subtitle && <p className="text-white/70 text-xs">{subtitle}</p>}
              {trend && (
                <div className="flex items-center gap-1 mt-2">
                  {trendUp ? (
                    <ArrowUpRight className="h-4 w-4 text-green-200" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-200" />
                  )}
                  <span
                    className={`text-sm font-semibold ${
                      trendUp ? "text-green-200" : "text-red-200"
                    }`}
                  >
                    {trend}
                  </span>
                </div>
              )}
            </div>
            <div className="bg-white/20 rounded-full p-3 backdrop-blur-sm">
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const PlanCard = ({ plan }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{plan.plan_name}</CardTitle>
          <Badge variant="secondary" className="text-lg font-bold">
            {plan.tenant_count}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">الإيرادات المحتملة</span>
            <span className="font-semibold text-green-600">
              {Number(plan.potential_revenue).toLocaleString("ar-IQ")} د.ع
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${
                  (plan.tenant_count / stats?.tenants?.total_tenants) * 100 || 0
                }%`,
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ExpiringSubscriptionCard = ({ subscription }) => {
    const isUrgent = subscription.days_remaining <= 3;

    return (
      <Card
        className={`hover:shadow-md transition-shadow ${
          isUrgent
            ? "border-red-200 bg-red-50/50"
            : "border-yellow-200 bg-yellow-50/50"
        }`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <h4 className="font-semibold text-slate-900">
                {subscription.name}
              </h4>
              <p className="text-sm text-slate-600">
                {subscription.subdomain}.lawsystem.com
              </p>
              <Badge variant="outline" className="text-xs">
                {subscription.plan_name}
              </Badge>
            </div>
            <Badge
              variant={isUrgent ? "destructive" : "default"}
              className="flex items-center gap-1"
            >
              <Clock className="h-3 w-3" />
              {subscription.days_remaining} يوم
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <p className="text-slate-600">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  const growthRate =
    stats.revenue.last_month > 0
      ? (
          ((stats.revenue.this_month - stats.revenue.last_month) /
            stats.revenue.last_month) *
          100
        ).toFixed(1)
      : 0;

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            لوحة التحكم
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            نظرة شاملة على نظام إدارة مكاتب المحاماة
          </p>
        </div>
        <Button
          onClick={fetchDashboardData}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          تحديث البيانات
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="إجمالي المستأجرين"
          value={stats.tenants.total_tenants}
          subtitle={`نشط: ${stats.tenants.active_tenants} مكتب`}
          icon={Building2}
          gradient="from-blue-500 to-blue-600"
          trend={`+${stats.new_tenants_this_month} هذا الشهر`}
          trendUp={true}
        />
        <StatCard
          title="الإيرادات الشهرية"
          value={`${(stats.revenue.this_month / 1000).toFixed(1)}K`}
          subtitle="دينار عراقي"
          icon={DollarSign}
          gradient="from-green-500 to-green-600"
          trend={`${growthRate > 0 ? "+" : ""}${growthRate}%`}
          trendUp={growthRate > 0}
        />
        <StatCard
          title="إجمالي المستخدمين"
          value={stats.tenants.total_users}
          subtitle={`موزعين على ${stats.tenants.active_tenants} مكتب`}
          icon={Users}
          gradient="from-orange-500 to-orange-600"
        />
        <StatCard
          title="اشتراكات منتهية قريباً"
          value={stats.expiring_subscriptions}
          subtitle="خلال 7 أيام القادمة"
          icon={AlertTriangle}
          gradient="from-red-500 to-red-600"
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="plans">توزيع الخطط</TabsTrigger>
          <TabsTrigger value="revenue">الإيرادات</TabsTrigger>
          <TabsTrigger value="expiring">المنتهية</TabsTrigger>
        </TabsList>

        {/* Plans Distribution */}
        <TabsContent value="plans" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>توزيع الاشتراكات حسب الخطط</CardTitle>
              <CardDescription>
                عرض تفصيلي لتوزيع المكاتب على الخطط المختلفة
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subscriptionStats.map((plan, index) => (
                  <PlanCard key={index} plan={plan} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue */}
        <TabsContent value="revenue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>الإيرادات (آخر 6 أشهر)</CardTitle>
              <CardDescription>تتبع الإيرادات الشهرية للنظام</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {revenue.map((month, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {month.month}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            إيرادات الشهر
                          </p>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="text-xl font-bold text-green-600">
                          {Number(month.total).toLocaleString("ar-IQ")}
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          دينار عراقي
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expiring Subscriptions */}
        <TabsContent value="expiring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                الاشتراكات المنتهية قريباً ({expiringSubscriptions.length})
              </CardTitle>
              <CardDescription>
                المكاتب التي تنتهي اشتراكاتها خلال 7 أيام
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiringSubscriptions.length === 0 ? (
                <div className="text-center py-12 space-y-3">
                  <div className="bg-green-100 dark:bg-green-900/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    لا توجد اشتراكات منتهية
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    جميع الاشتراكات نشطة ولا يوجد ما يستدعي القلق
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {expiringSubscriptions.map((sub) => (
                      <ExpiringSubscriptionCard
                        key={sub.id}
                        subscription={sub}
                      />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdminDashboard;
